import React, { useState, useMemo, useEffect, Suspense, lazy } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { Container, useMediaQuery, CircularProgress, Box } from "@mui/material";
import { DataProvider } from "./contexts/DataContext";
import { getDesignTokens, getThemedComponents } from "./conf/theme.ts";
import {
  createTheme,
  ThemeProvider,
  responsiveFontSizes,
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { ColorModeContext } from "./contexts/ColorModeContext";
import { TourProvider } from "./contexts/TourContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SecureRoute from "./components/SecureRoute.js";
import sharedConfig from "../shared-config.json";
import ResponsiveAppBar from "./components/appBar";
import LoginDialog from "./components/LoginDialog";
import CachedUploadsNotification from "./components/CachedUploadsNotification";

// Only use code splitting for major route-based components
const Home = lazy(() => import(/* webpackChunkName: "home" */ "./pages/Home"));
const Participant = lazy(() =>
  import(/* webpackChunkName: "participant" */ "./pages/Participant")
);
const ParticipantRegister = lazy(() =>
  import(/* webpackChunkName: "participant" */ "./pages/ParticipantRegister")
);
const Admin = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/Admin")
);
const QRRegistration = lazy(() =>
  import(/* webpackChunkName: "enrollment" */ "./pages/QRRegistration")
);
const ConsentReturn = lazy(() =>
  import(/* webpackChunkName: "enrollment" */ "./pages/ConsentReturn")
);
const StudyManagement = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/StudyManagement")
);

// Loading component for suspense fallback
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "calc(100vh - 64px)",
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const [mode, setMode] = useState("light");
  const [loginOpen, setLoginOpen] = useState(false);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  useEffect(() => {
    setMode("light");
    //setMode(prefersDarkMode ? "dark" : "light");
  }, [prefersDarkMode]);

  let theme = useMemo(
    () =>
      responsiveFontSizes(
        createTheme(deepmerge(getDesignTokens(mode), getThemedComponents(mode)))
      ),
    [mode]
  );

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const handleLoginClick = () => {
    setLoginOpen(true);
  };

  const handleLoginClose = () => {
    setLoginOpen(false);
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <CssBaseline />
      <ThemeProvider theme={theme}>
        <DataProvider>
          <BrowserRouter>
            {/* Wraps both the bar and the routes: the participant page owns
                the tour, but the link that reopens it is in the bar. */}
            <TourProvider>
              <ResponsiveAppBar onLoginClick={handleLoginClick} />
              <LoginDialog open={loginOpen} onClose={handleLoginClose} />
              <CachedUploadsNotification />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Admin/PI routes */}
                  <Route
                    path="/admin"
                    element={
                      <SecureRoute
                        roleRequired={["MyMeds.Admin", "MyMeds.PI"]}
                        element={<Admin />}
                      />
                    }
                  />
                  <Route
                    path="/admin/studies"
                    element={
                      <SecureRoute
                        roleRequired={["MyMeds.Admin", "MyMeds.PI"]}
                        element={<StudyManagement />}
                      />
                    }
                  />

                  {/* Public routes. The FAQs and contact details now live on
                      the landing page, so the old paths point at its sections. */}
                  <Route
                    path="/contact"
                    element={<Navigate to="/#contact" replace />}
                  />
                  <Route path="/faq" element={<Navigate to="/#faq" replace />} />
                  <Route
                    path="/"
                    element={<Home onLoginClick={handleLoginClick} />}
                  />
                  <Route
                    path="/login"
                    element={
                      <Navigate to="/" state={{ showLogin: true }} replace />
                    }
                  />

                  {/* Participant routes */}
                  <Route
                    path="/participant"
                    element={
                      <SecureRoute
                        roleRequired={[sharedConfig.roles.participant.name]}
                        element={<Participant />}
                        excludeRoles={[
                          sharedConfig.roles.admin.name,
                          sharedConfig.roles.pi.name,
                        ]}
                      />
                    }
                  />
                  <Route path="/register" element={<ParticipantRegister />} />
                  <Route
                    path="/participant-register"
                    element={<ParticipantRegister />}
                  />

                  {/* Enrollment flow routes */}
                  <Route path="/qr-registration/:hash" element={<QRRegistration />} />
                  <Route path="/consent-return" element={<ConsentReturn />} />

                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </TourProvider>
          </BrowserRouter>
        </DataProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
